// src/components/operationCenter/widgets/PortfolioNavWidget.tsx
// Portfolio Net Asset Value chart widget

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import * as echarts from "echarts";
import type { EChartsOption, ECharts } from "echarts";
import type { WidgetProps, PortfolioNavConfig } from "@/types/dashboard";
import { usePortfolioNav, usePortfolioPerformanceVsBenchmark } from "@/hooks/portfolio";
import { useAccessToken } from "@/hooks/useAccessToken";
import type { NavPoint, ReturnPoint } from "@/types/portfolio";
import { WIDGET_EMPTY } from "../WidgetWrapper";

type ViewMode = "nav" | "performance";
type TimeRange = "7d" | "30d" | "90d" | "365d" | "all";

const TIME_RANGES: { label: string; value: TimeRange; days: number }[] = [
  { label: "7D", value: "7d", days: 7 },
  { label: "30D", value: "30d", days: 30 },
  { label: "90D", value: "90d", days: 90 },
  { label: "1Y", value: "365d", days: 365 },
  { label: "All", value: "all", days: 0 },
];

const PortfolioNavWidget: React.FC<WidgetProps<PortfolioNavConfig>> = ({ 
  widget, 
  onConfigChange 
}) => {
  const config = widget.config as PortfolioNavConfig;
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<ECharts | null>(null);
  
  const [viewMode, setViewMode] = useState<ViewMode>("nav");
  const [timeRange, setTimeRange] = useState<TimeRange>(
    (config.timeframe?.replace("1w", "7d").replace("1m", "30d").replace("3m", "90d").replace("6m", "180d").replace("1y", "365d") as TimeRange) || "all"
  );

  const authToken = useAccessToken();
  
  // Fetch NAV data
  const { data: navData, isLoading: navLoading, error: navError } = usePortfolioNav(authToken);
  
  // Fetch performance vs benchmark
  const { data: perfVsData, isLoading: perfVsLoading, error: perfVsError } = usePortfolioPerformanceVsBenchmark(authToken);

  // Process NAV data
  const navChartData = useMemo(() => {
    const series: NavPoint[] = navData?.data?.performance_data ?? [];
    if (series.length === 0) return { dates: [], values: [] };

    const days = TIME_RANGES.find(r => r.value === timeRange)?.days || 0;
    
    if (days === 0) {
      return {
        dates: series.map(p => p.date),
        values: series.map(p => p.portfolio_value),
      };
    }

    const lastDate = series[series.length - 1]?.date;
    const cutoff = new Date(new Date(lastDate + "T00:00:00Z").getTime() - days * 24 * 60 * 60 * 1000);

    const filtered = series.filter(p => new Date(p.date + "T00:00:00Z") >= cutoff);
    return {
      dates: filtered.map(p => p.date),
      values: filtered.map(p => p.portfolio_value),
    };
  }, [navData, timeRange]);

  // Process performance vs benchmark data
  const perfChartData = useMemo(() => {
    const rangeMap: Record<string, string> = { '7d': '7d', '30d': '30d', '90d': '90d', '365d': '365d', 'all': 'all' };
    const windowKey = rangeMap[timeRange] ?? 'all';
    const selected = perfVsData?.data?.series?.find((s: { window: string }) => s.window === windowKey);
    const returns: ReturnPoint[] = selected?.returns ?? [];
    
    if (returns.length === 0) return { dates: [], portfolioRets: [], btcRets: [] };
    
    return {
      dates: returns.map(p => p.date),
      portfolioRets: returns.map(p => Math.round((p.portfolio_return ?? 0) * 10000) / 100),
      btcRets: returns.map(p => Math.round((p.btc_return ?? 0) * 10000) / 100),
    };
  }, [perfVsData, timeRange]);

  // Handlers
  const handleTimeRangeChange = useCallback((range: TimeRange) => {
    setTimeRange(range);
    const tfMap: Record<string, string> = { '7d': '1w', '30d': '1m', '90d': '3m', '365d': '1y', 'all': 'all' };
    onConfigChange?.({ timeframe: tfMap[range] as PortfolioNavConfig['timeframe'] });
  }, [onConfigChange]);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    onConfigChange?.({ show_benchmark: mode === 'performance' });
  }, [onConfigChange]);

  // Render chart
  useEffect(() => {
    if (!chartRef.current) return;

    const chartData = viewMode === 'nav' ? navChartData : perfChartData;
    if (chartData.dates.length === 0) return;

    const chart = chartInstanceRef.current || echarts.init(chartRef.current);
    chartInstanceRef.current = chart;

    let option: EChartsOption;

    if (viewMode === 'nav') {
      option = {
        backgroundColor: "transparent",
        grid: { top: 20, right: 16, bottom: 24, left: 16, containLabel: true },
        tooltip: {
          trigger: "axis",
          backgroundColor: "rgba(17, 24, 39, 0.95)",
          borderColor: "#374151",
          textStyle: { color: "#f3f4f6", fontSize: 11 },
          formatter: (params: unknown) => {
            if (!Array.isArray(params) || params.length === 0) return "";
            const p = params[0] as { axisValue: string; value: number };
            const val = p.value;
            return `<div style="font-weight:500;margin-bottom:6px;color:#9ca3af;">${p.axisValue}</div>
              <div style="font-weight:600;">NAV: $${val.toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>`;
          },
        },
        xAxis: {
          type: "category",
          data: navChartData.dates,
          axisLine: { lineStyle: { color: "#374151" } },
          axisLabel: { color: "#6b7280", fontSize: 10 },
          splitLine: { show: false },
        },
        yAxis: {
          type: "value",
          axisLine: { show: false },
          axisLabel: {
            color: "#6b7280",
            fontSize: 10,
            formatter: (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v.toFixed(0)}`,
          },
          splitLine: { lineStyle: { color: "#374151", type: "dashed" } },
        },
        series: [{
          type: "line",
          data: navChartData.values,
          smooth: true,
          symbol: "none",
          lineStyle: { color: "#818cf8", width: 2 },
          areaStyle: {
            color: {
              type: "linear",
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(129, 140, 248, 0.3)" },
                { offset: 1, color: "rgba(129, 140, 248, 0.05)" },
              ],
            },
          },
        }],
      };
    } else {
      // Performance vs BTC view
      option = {
        backgroundColor: "transparent",
        grid: { top: 30, right: 16, bottom: 24, left: 16, containLabel: true },
        legend: {
          show: true,
          top: 0,
          left: "center",
          textStyle: { color: "#9ca3af", fontSize: 10 },
          itemWidth: 16,
          itemHeight: 8,
          icon: "roundRect",
        },
        tooltip: {
          trigger: "axis",
          backgroundColor: "rgba(17, 24, 39, 0.95)",
          borderColor: "#374151",
          textStyle: { color: "#f3f4f6", fontSize: 11 },
          formatter: (params: unknown) => {
            if (!Array.isArray(params) || params.length === 0) return "";
            const p = params[0] as { axisValue: string };
            let html = `<div style="font-weight:500;margin-bottom:6px;color:#9ca3af;">${p.axisValue}</div>`;
            params.forEach((item: unknown) => {
              const i = item as { seriesName: string; value: number; color: string };
              html += `<div style="display:flex;justify-content:space-between;gap:16px;">
                <span><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${i.color};margin-right:6px;"></span>${i.seriesName}</span>
                <span style="font-weight:600;">${i.value >= 0 ? '+' : ''}${i.value.toFixed(2)}%</span>
              </div>`;
            });
            return html;
          },
        },
        xAxis: {
          type: "category",
          data: perfChartData.dates,
          axisLine: { lineStyle: { color: "#374151" } },
          axisLabel: { color: "#6b7280", fontSize: 10 },
          splitLine: { show: false },
        },
        yAxis: {
          type: "value",
          axisLine: { show: false },
          axisLabel: {
            color: "#6b7280",
            fontSize: 10,
            formatter: (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`,
          },
          splitLine: { lineStyle: { color: "#374151", type: "dashed" } },
        },
        series: [
          {
            name: "Portfolio",
            type: "line",
            data: perfChartData.portfolioRets,
            smooth: true,
            symbol: "none",
            lineStyle: { color: "#818cf8", width: 2 },
          },
          {
            name: "BTC",
            type: "line",
            data: perfChartData.btcRets,
            smooth: true,
            symbol: "none",
            lineStyle: { color: "#f59e0b", width: 2, type: "dashed" },
          },
        ],
      };
    }

    chart.setOption(option, true);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [viewMode, navChartData, perfChartData]);

  // Cleanup
  useEffect(() => {
    return () => {
      chartInstanceRef.current?.dispose();
    };
  }, []);

  const isLoading = viewMode === 'nav' ? navLoading : perfVsLoading;
  const error = viewMode === 'nav' ? navError : perfVsError;
  const hasData = viewMode === 'nav' 
    ? navChartData.dates.length > 0 
    : perfChartData.dates.length > 0;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Title */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
          {widget.title ?? "My Portfolio NAV"}
        </span>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/30">
        {/* View mode toggle */}
        <div className="flex gap-0.5 bg-slate-800/40 rounded p-0.5">
          {(['nav', 'performance'] as ViewMode[]).map(mode => (
            <button
              key={mode}
              onClick={() => handleViewModeChange(mode)}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                viewMode === mode
                  ? "bg-indigo-500/25 text-indigo-300"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {mode === 'nav' ? 'NAV' : 'vs BTC'}
            </button>
          ))}
        </div>

        {/* Time range */}
        <div className="flex gap-0.5 ml-auto">
          {TIME_RANGES.map(r => (
            <button
              key={r.value}
              onClick={() => handleTimeRangeChange(r.value)}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                timeRange === r.value
                  ? "bg-blue-500/20 text-blue-300"
                  : "text-slate-400 hover:bg-slate-700/50"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-0 p-2">
        {isLoading ? (
          <div className={WIDGET_EMPTY}>
            Loading portfolio data...
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center text-red-400 text-sm">
            Failed to load portfolio data
          </div>
        ) : !hasData ? (
          <div className={WIDGET_EMPTY}>
            No portfolio data available
          </div>
        ) : (
          <div ref={chartRef} className="h-full w-full" />
        )}
      </div>
    </div>
  );
};

export default PortfolioNavWidget;
